import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import {
  ChevronDown,
  Eye,
  EyeOff,
  FileJson,
  FileSpreadsheet,
  List,
  Network,
  Plus,
  RefreshCw,
  Server,
  Settings2,
  Upload,
} from 'lucide-react'
import type { ColumnDefinition } from '../case-infrastructure-tab.helpers'

interface TitleSectionProps {
  endpointsCount: number
  relationshipsCount: number
  viewMode: 'list' | 'map'
  setViewMode: (mode: 'list' | 'map') => void
  t: TFunction<'cases'>
}

export function TitleSection({
  endpointsCount,
  relationshipsCount,
  viewMode,
  setViewMode,
  t,
}: TitleSectionProps): ReactElement {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-lime-100 dark:bg-lime-950/50">
        <Server className="h-5 w-5 text-lime-600 dark:text-lime-400" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {t('infrastructure.title')}
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t('infrastructure.endpointCount', { count: endpointsCount })} |{' '}
          {t('infrastructure.relationshipCount', { count: relationshipsCount })}
        </p>
      </div>
      <ViewModeToggle viewMode={viewMode} setViewMode={setViewMode} t={t} />
    </div>
  )
}

interface ViewModeToggleProps {
  viewMode: 'list' | 'map'
  setViewMode: (mode: 'list' | 'map') => void
  t: TFunction<'cases'>
}

function ViewModeToggle({ viewMode, setViewMode, t }: ViewModeToggleProps): ReactElement {
  const baseClass =
    'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors'
  const activeClass = 'bg-lime-100 dark:bg-[#8a9bba]/10 text-lime-700 dark:text-lime-300'
  const inactiveClass =
    'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
  return (
    <div className="ml-4 flex items-center rounded-lg border border-slate-200 p-1 dark:border-slate-700">
      <button
        onClick={() => {
          setViewMode('list')
        }}
        className={`${baseClass} ${viewMode === 'list' ? activeClass : inactiveClass}`}
      >
        <List className="h-4 w-4" />
        {t('infrastructure.viewList')}
      </button>
      <button
        onClick={() => {
          setViewMode('map')
        }}
        className={`${baseClass} ${viewMode === 'map' ? activeClass : inactiveClass}`}
      >
        <Network className="h-4 w-4" />
        {t('infrastructure.viewMap')}
      </button>
    </div>
  )
}

interface RefreshButtonProps {
  refetch: () => unknown
  t: TFunction<'cases'>
}

export function RefreshButton({ refetch, t }: RefreshButtonProps): ReactElement {
  return (
    <button
      onClick={() => {
        void refetch()
      }}
      className="rounded-lg border border-slate-300 p-2 text-slate-500 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
      title={t('actions.refresh')}
    >
      <RefreshCw className="h-4 w-4" />
    </button>
  )
}

interface ColumnSettingsProps {
  showColumnSettings: boolean
  setShowColumnSettings: (v: boolean | ((prev: boolean) => boolean)) => void
  visibleColumns: Set<string>
  toggleColumn: (id: string) => void
  resetColumns: () => void
  columnDefinitions: ColumnDefinition[]
  t: TFunction<'cases'>
}

export function ColumnSettings(props: ColumnSettingsProps): ReactElement {
  const {
    showColumnSettings,
    setShowColumnSettings,
    visibleColumns,
    toggleColumn,
    resetColumns,
    columnDefinitions,
    t,
  } = props
  return (
    <div className="relative">
      <button
        onClick={() => {
          setShowColumnSettings((prev) => !prev)
        }}
        className="rounded-lg border border-slate-300 p-2 text-slate-500 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
        title={t('infrastructure.columnSettings')}
      >
        <Settings2 className="h-4 w-4" />
      </button>
      {showColumnSettings && (
        <ColumnSettingsPanel
          visibleColumns={visibleColumns}
          toggleColumn={toggleColumn}
          resetColumns={resetColumns}
          columnDefinitions={columnDefinitions}
          t={t}
        />
      )}
    </div>
  )
}

interface ColumnSettingsPanelProps {
  visibleColumns: Set<string>
  toggleColumn: (id: string) => void
  resetColumns: () => void
  columnDefinitions: ColumnDefinition[]
  t: TFunction<'cases'>
}

function ColumnSettingsPanel(props: ColumnSettingsPanelProps): ReactElement {
  const { visibleColumns, toggleColumn, resetColumns, columnDefinitions, t } = props
  return (
    <div className="absolute top-full right-0 z-20 mt-1 w-56 rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800">
      <div className="border-b border-slate-200 px-3 py-2 dark:border-slate-700">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            {t('infrastructure.visibleColumns')}
          </span>
          <button
            onClick={resetColumns}
            className="text-xs text-lime-600 hover:underline dark:text-lime-400"
          >
            {t('infrastructure.reset')}
          </button>
        </div>
      </div>
      <div className="max-h-64 overflow-y-auto py-1">
        {columnDefinitions.map((col) => (
          <ColumnRow
            key={col.id}
            col={col}
            selected={visibleColumns.has(col.id)}
            onToggle={() => {
              toggleColumn(col.id)
            }}
            t={t}
          />
        ))}
      </div>
    </div>
  )
}

interface ColumnRowProps {
  col: ColumnDefinition
  selected: boolean
  onToggle: () => void
  t: TFunction<'cases'>
}

function ColumnRow({ col, selected, onToggle, t }: ColumnRowProps): ReactElement {
  return (
    <button
      onClick={onToggle}
      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700"
    >
      {selected ? (
        <Eye className="h-4 w-4 text-lime-600 dark:text-lime-400" />
      ) : (
        <EyeOff className="h-4 w-4 text-slate-400" />
      )}
      <span className={selected ? '' : 'text-slate-400'}>{col.label}</span>
      {col.itSystemOnly && (
        <span className="ml-auto text-xs text-slate-400 dark:text-slate-500">
          {t('infrastructure.itOnly')}
        </span>
      )}
    </button>
  )
}

export interface ImportDropdownActions {
  setShowNewImportModal: (v: boolean) => void
  setShowRelationshipImportModal: (v: boolean) => void
  setShowCsvImportModal: (v: boolean) => void
  setShowJsonImportModal: (v: boolean) => void
}

interface ImportDropdownProps {
  showImportDropdown: boolean
  setShowImportDropdown: (v: boolean | ((prev: boolean) => boolean)) => void
  actions: ImportDropdownActions
  t: TFunction<'cases'>
}

export function ImportDropdown({
  showImportDropdown,
  setShowImportDropdown,
  actions,
  t,
}: ImportDropdownProps): ReactElement {
  return (
    <div className="relative">
      <button
        onClick={() => {
          setShowImportDropdown((prev) => !prev)
        }}
        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
      >
        <Upload className="h-4 w-4" />
        {t('infrastructure.importButton')}
        <ChevronDown className="h-4 w-4" />
      </button>
      {showImportDropdown && (
        <ImportDropdownPanel
          actions={actions}
          close={() => {
            setShowImportDropdown(false)
          }}
          t={t}
        />
      )}
    </div>
  )
}

interface ImportDropdownPanelProps {
  actions: ImportDropdownActions
  close: () => void
  t: TFunction<'cases'>
}

function ImportDropdownPanel({ actions, close, t }: ImportDropdownPanelProps): ReactElement {
  const open = (setter: (v: boolean) => void) => () => {
    setter(true)
    close()
  }
  return (
    <div className="absolute top-full right-0 z-10 mt-1 w-56 rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800">
      <button
        onClick={open(actions.setShowNewImportModal)}
        className="flex w-full items-center gap-2 rounded-t-lg px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700"
      >
        <Upload className="h-4 w-4" />
        {t('infrastructure.importFromFile')}
        <span className="ml-auto text-xs font-medium text-lime-600 dark:text-lime-400">
          {t('infrastructure.new')}
        </span>
      </button>
      <div className="border-t border-slate-200 dark:border-slate-700">
        <button
          onClick={open(actions.setShowRelationshipImportModal)}
          className="flex w-full items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700"
        >
          <Upload className="h-4 w-4" />
          {t('infrastructure.importRelationship.dropdownLabel')}
        </button>
      </div>
      <LegacyImportSection actions={actions} open={open} t={t} />
      <QuestionnaireImport t={t} />
    </div>
  )
}

interface LegacyImportSectionProps {
  actions: ImportDropdownActions
  open: (setter: (v: boolean) => void) => () => void
  t: TFunction<'cases'>
}

function LegacyImportSection({ actions, open, t }: LegacyImportSectionProps): ReactElement {
  return (
    <div className="border-t border-slate-200 dark:border-slate-700">
      <p className="px-4 py-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">
        {t('infrastructure.legacyImport')}
      </p>
      <button
        onClick={open(actions.setShowCsvImportModal)}
        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-700"
      >
        <FileSpreadsheet className="h-4 w-4" />
        {t('infrastructure.importFromCsv')}
      </button>
      <button
        onClick={open(actions.setShowJsonImportModal)}
        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-700"
      >
        <FileJson className="h-4 w-4" />
        {t('infrastructure.importFromJson')}
      </button>
    </div>
  )
}

function QuestionnaireImport({ t }: { t: TFunction<'cases'> }): ReactElement {
  return (
    <div className="border-t border-slate-200 dark:border-slate-700">
      <button
        disabled
        className="flex w-full cursor-not-allowed items-center gap-2 rounded-b-lg px-4 py-2 text-sm text-slate-400 dark:text-slate-500"
        title="Coming soon - will import from client questionnaire responses"
      >
        <Server className="h-4 w-4" />
        {t('infrastructure.fromQuestionnaire')}
      </button>
    </div>
  )
}

interface AddEndpointButtonProps {
  setShowCreateModal: (v: boolean) => void
  t: TFunction<'cases'>
}

export function AddEndpointButton({ setShowCreateModal, t }: AddEndpointButtonProps): ReactElement {
  return (
    <button
      onClick={() => {
        setShowCreateModal(true)
      }}
      className="inline-flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5c6e94]"
    >
      <Plus className="h-4 w-4" />
      {t('infrastructure.addEndpoint')}
    </button>
  )
}
